import React from "react"
import { Flag, Loader2 } from "lucide-react"
import type { Attempt } from "../types"
import { ATTEMPT_STATUS_CONFIG } from "../utils/attemptStatus"
import "./AttemptHistoryTable.style.scss"

interface AttemptHistoryTableProps {
  attempts: Attempt[]
  onSelect: (attemptId: string) => void
  canStartNew: boolean
  onNewAttempt: () => void
  onBack: () => void
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-IE", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })

const formatTime = (seconds: number) => {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}m ${s}s`
}

const AttemptHistoryTable = ({
  attempts,
  onSelect,
  canStartNew,
  onNewAttempt,
  onBack,
}: AttemptHistoryTableProps) => (
  <div className="attempt-history">
    <div className="attempt-history__header">
      <h3>
        Attempt History
        <span>
          ({attempts.length} attempt{attempts.length !== 1 ? "s" : ""})
        </span>
      </h3>
      {canStartNew && (
        <button type="button" className="is-primary" onClick={onNewAttempt}>
          New Attempt
        </button>
      )}
    </div>

    <div className="attempt-history__table-wrap">
      <table>
        <thead>
          <tr>
            <th>No.</th>
            <th>Date</th>
            <th>Time</th>
            <th>Status</th>
            <th>Score</th>
            <th className="is-right">Action</th>
          </tr>
        </thead>
        <tbody>
          {attempts.map((a) => {
            const cfg = ATTEMPT_STATUS_CONFIG[a.status]
            return (
              <tr key={a.id}>
                <td className="is-num">#{a.attemptNumber}</td>
                <td>{formatDate(a.startedAt)}</td>
                <td>{formatTime(a.timeSpentSeconds)}</td>
                <td>
                  <span className={`attempt-history__status ${cfg.className}`}>
                    {a.status === "marking" && (
                      <Loader2 size={12} className="is-spinning" />
                    )}
                    {a.status === "flagged_for_review" && <Flag size={12} />}
                    {cfg.label}
                  </span>
                </td>
                <td>
                  {a.status === "marking" ? (
                    <span className="is-awaiting">
                      <Loader2 size={12} className="is-spinning" /> Awaiting AI
                    </span>
                  ) : a.status === "submitted" ? (
                    <span className="is-muted">No AI</span>
                  ) : a.score !== null && a.maxScore !== null ? (
                    <strong
                      className={
                        a.score === a.maxScore ? "is-full" : "is-partial"
                      }
                    >
                      {a.score}/{a.maxScore}
                    </strong>
                  ) : (
                    <span className="is-muted">—</span>
                  )}
                </td>
                <td className="is-right">
                  <button type="button" onClick={() => onSelect(a.id)}>
                    {a.status === "draft" ? "Continue" : "View"}
                  </button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>

    <button type="button" className="attempt-history__back" onClick={onBack}>
      ← Back to attempt
    </button>
  </div>
)

export default AttemptHistoryTable
